import { TRPCClientError } from '@/utils/trpc'
import { KeyValueStorage } from '@/utils/storage/KeyValueStorage'

type CacheKey = { group: string; key: string }

export class SimpleCache {
    storage: KeyValueStorage

    constructor(storage: KeyValueStorage) {
        this.storage = storage
    }

    async get<TData>(
        key: CacheKey,
        query: (updateAfter?: Date) => Promise<TData>,
        update: (data: TData) => void,
        {
            hasAccess,
            ignoreCachedData,
            ignoreNotFoundError,
            onAsyncError,
        }: {
            hasAccess?: boolean
            ignoreCachedData?: boolean
            ignoreNotFoundError?: boolean
            onAsyncError?: (error: Error) => void
        } = {}
    ): Promise<void> {
        // If has no access then clear the cache and return
        if (hasAccess === false) {
            await this.removeFromStorage(key)
            return
        }

        const cached = await this.tryLoadFromStorage(key)

        if (cached && !ignoreCachedData) {
            // Immediatly update witch cached value
            update(cached.data as TData)

            // Query in background for an updatd value
            query(cached.timestamp)
                .then(async (data) => {
                    // Store new value
                    await this.saveToStorage(key, data)

                    // Update now with updated value
                    update(data as TData)
                })
                .catch(async (error) => {
                    if (error instanceof TRPCClientError) {
                        if (error.data?.code === 'NOT_FOUND') {
                            // ignore not modified as that indicates no updates
                            if (error.shape?.message === 'NOT_MODIFIED') {
                                return
                            } else {
                                // if not found we remove from cache
                                await this.clear(key.group)

                                if (ignoreNotFoundError) {
                                    return
                                }
                            }
                        }
                    }

                    if (onAsyncError) {
                        onAsyncError(error)
                    } else {
                        throw error
                    }
                })
        } else {
            let data
            try {
                // Wait to get first time value
                data = await query()

                // Store it into cache
                await this.saveToStorage(key, data)

                // Return it by calling our update initially
                update(data)
            } catch (error) {
                if (error instanceof TRPCClientError && error.data?.code === 'NOT_FOUND' && ignoreNotFoundError) {
                    return
                } else {
                    throw error
                }
            }
        }
    }

    private async saveToStorage(key: CacheKey, data: any) {
        await this.storage.set(`cache.${key.group}.${key.key}`, { timestamp: new Date(), data })
    }

    public async removeFromStorage(key: CacheKey) {
        await this.storage.remove(`cache.${key.group}.${key.key}`)
    }

    private async tryLoadFromStorage(key: CacheKey): Promise<{ timestamp: Date; data: any } | null> {
        const value = await this.storage.get<{ timestamp: Date; data: any }>(`cache.${key.group}.${key.key}`)

        return value ?? null
    }

    public clear(group: string) {
        this.storage.remove(`cache.${group}.*`)
    }
}
